import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useCategories } from '../../hooks/useCategories';
import { supabase } from '../../lib/supabase';
import posthog from '../../lib/posthog';
import {
  Button,
  CategoryBadge,
  Spinner,
  Toast,
} from '../../components/design-system';
import { PlaceDetailsEditor } from '../../components/host/PlaceDetailsEditor';
import type { PlaceFieldsValue } from '../../lib/placePicker';
import { PhotoGalleryField, PhotoCarousel } from '../../components/design-system';
import { getPlacePhotos, photoFields, uploadPlacePhoto } from '../../lib/placePhotos';
import type { EventLibraryItem } from '../../types';

type FormData = Omit<EventLibraryItem, 'id' | 'host_id' | 'created_at' | 'updated_at'>;

const EMPTY_FORM: FormData = {
  name: '', category_slug: 'food', description: '',
  address: null, latitude: null, longitude: null, photos: [], photo_url: null, photo_focal: null, tip: null,
};

/** "★ 4.6 (12)" — the average of all travelers' ratings for a place; nothing until it has one. */
function RatingSummary({ stat }: { stat?: { avg: number; count: number } }) {
  if (!stat) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-app-fg" title={`${stat.count} rating${stat.count === 1 ? '' : 's'}`}>
      <i className="ti ti-star-filled" style={{ color: 'var(--accent)', fontSize: 12 }} />
      {stat.avg.toFixed(1)} <span className="text-app-muted">({stat.count})</span>
    </span>
  );
}

export function EventLibrary() {
  const { user } = useAuth();
  const { categories } = useCategories();
  const [items, setItems] = useState<EventLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<(FormData & { id?: string }) | null>(null);
  const [saving, setSaving] = useState(false);
  const [catFilter, setCatFilter] = useState('');
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'list'>(() => (localStorage.getItem('sk_event_lib_view') as 'grid' | 'list') || 'grid');
  const [toast, setToast] = useState('');
  const [stats, setStats] = useState<Record<string, { avg: number; count: number }>>({});

  function flashToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  async function load() {
    if (!user) return;
    const { data } = await supabase
      .from('event_library')
      .select('*')
      .eq('host_id', user.id)
      .order('created_at', { ascending: false });
    setItems((data ?? []) as EventLibraryItem[]);
    const { data: rated } = await supabase.rpc('host_place_rating_stats');
    setStats(Object.fromEntries(
      ((rated ?? []) as { library_item_id: string; avg_rating: number; rating_count: number }[])
        .map((r) => [r.library_item_id, { avg: Number(r.avg_rating), count: Number(r.rating_count) }]),
    ));
    setLoading(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [user]);

  function applyPlacePatch(patch: Partial<PlaceFieldsValue>) {
    setEditing((prev) => {
      if (!prev) return prev;
      const { category, ...rest } = patch;
      return { ...prev, ...rest, ...(category !== undefined ? { category_slug: category } : {}) };
    });
  }

  async function handleSave() {
    if (!editing || !user) return;
    setSaving(true);
    const payload = {
      name: editing.name,
      category_slug: editing.category_slug,
      description: editing.description,
      address: editing.address || null,
      latitude: editing.latitude,
      longitude: editing.longitude,
      ...photoFields(editing.photos ?? []),
      tip: editing.tip || null,
      updated_at: new Date().toISOString(),
    };
    const isNewItem = !editing.id;
    const { error } = isNewItem
      ? await supabase.from('event_library').insert({ ...payload, host_id: user.id })
      : await supabase.from('event_library').update(payload).eq('id', editing.id);
    if (error) {
      setSaving(false);
      flashToast("Couldn't save — please try again.");
      return;
    }
    posthog.capture('event_library_item_saved', {
      action: isNewItem ? 'created' : 'updated',
      category: editing.category_slug,
      has_photo: (editing.photos ?? []).length > 0,
      photo_count: (editing.photos ?? []).length,
    });
    setEditing(null);
    setSaving(false);
    await load();
  }

  async function handleDelete(id: string) {
    const { error } = await supabase.from('event_library').delete().eq('id', id);
    if (error) {
      // Previously left `deleting` set on failure, silently stranding the
      // confirm modal open with no explanation — only dismissible by
      // clicking outside it.
      setDeleting(null);
      flashToast("Couldn't delete — please try again.");
      return;
    }
    posthog.capture('event_library_item_deleted');
    setDeleting(null);
    await load();
  }

  const filtered = items.filter((i) => {
    if (catFilter && i.category_slug !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        i.name.toLowerCase().includes(q) ||
        (i.address ?? '').toLowerCase().includes(q) ||
        (i.description ?? '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const catName = (slug: string) => categories.find((c) => c.slug === slug)?.name ?? slug;

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  const inputClass = 'rounded-md border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none';

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Event Library</h1>
            <p className="text-app-muted text-sm">
              {items.length} {items.length === 1 ? 'place' : 'places'} saved. Add them to any trip with one click.
            </p>
          </div>
          <Button className="shrink-0" onClick={() => setEditing({ ...EMPTY_FORM, category_slug: categories[0]?.slug ?? 'food' })}>
            + New place
          </Button>
        </div>

        {/* Search + view toggle */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-app-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, address…"
              className="w-full rounded-full border border-app-border bg-app-bg pl-9 pr-3 py-2 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none"
            />
          </div>
          <div className="flex rounded-lg border border-app-border overflow-hidden shrink-0">
            <button
              onClick={() => { setView('grid'); localStorage.setItem('sk_event_lib_view', 'grid'); }}
              className={`p-3 md:p-2 transition-colors ${view === 'grid' ? 'bg-app-fg/10 text-app-fg' : 'text-app-muted hover:text-app-fg'}`}
              title="Grid view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M4.25 2A2.25 2.25 0 002 4.25v2.5A2.25 2.25 0 004.25 9h2.5A2.25 2.25 0 009 6.75v-2.5A2.25 2.25 0 006.75 2h-2.5zm0 9A2.25 2.25 0 002 13.25v2.5A2.25 2.25 0 004.25 18h2.5A2.25 2.25 0 009 15.75v-2.5A2.25 2.25 0 006.75 11h-2.5zm9-9A2.25 2.25 0 0011 4.25v2.5A2.25 2.25 0 0013.25 9h2.5A2.25 2.25 0 0018 6.75v-2.5A2.25 2.25 0 0015.75 2h-2.5zm0 9A2.25 2.25 0 0011 13.25v2.5A2.25 2.25 0 0013.25 18h2.5A2.25 2.25 0 0018 15.75v-2.5A2.25 2.25 0 0015.75 11h-2.5z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={() => { setView('list'); localStorage.setItem('sk_event_lib_view', 'list'); }}
              className={`p-3 md:p-2 transition-colors ${view === 'list' ? 'bg-app-fg/10 text-app-fg' : 'text-app-muted hover:text-app-fg'}`}
              title="List view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 5A.75.75 0 012.75 9h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 9.75zm0 5a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex gap-2 md:flex-wrap overflow-x-auto md:overflow-visible -mx-6 px-6 md:mx-0 md:px-0 mb-6">
          <button
            onClick={() => setCatFilter('')}
            className={`shrink-0 whitespace-nowrap min-h-[40px] md:min-h-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${!catFilter ? 'bg-app-fg/10 text-app-fg' : 'text-app-muted hover:text-app-fg'}`}
          >
            All ({items.length})
          </button>
          {categories.map((cat) => {
            const count = items.filter((i) => i.category_slug === cat.slug).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.slug}
                onClick={() => setCatFilter(catFilter === cat.slug ? '' : cat.slug)}
                className={`shrink-0 whitespace-nowrap min-h-[40px] md:min-h-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${catFilter === cat.slug ? 'bg-app-fg/10 text-app-fg' : 'text-app-muted hover:text-app-fg'}`}
              >
                {cat.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Items */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-sm text-app-muted">
              {items.length === 0 ? 'No places saved yet. Start building your library.' : 'No places match your search.'}
            </p>
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((item) => (
              <div key={item.id} className="bg-app-card border border-app-border rounded-2xl overflow-hidden flex flex-col">
                {getPlacePhotos(item).length > 0 ? (
                  <div className="relative aspect-video bg-app-border">
                    <PhotoCarousel photos={getPlacePhotos(item)} alt={item.name} />
                  </div>
                ) : (
                  <div className="aspect-video bg-app-border/50 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-8 h-8 text-app-muted/30">
                      <path fillRule="evenodd" d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.69l-2.22-2.219a.75.75 0 00-1.06 0l-1.91 1.909-3.22-3.22a.75.75 0 00-1.06 0L2.5 11.06zm12.25-3.31a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                <div className="p-4 flex flex-col gap-2 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-app-fg">{item.name}</h3>
                    <CategoryBadge label={catName(item.category_slug)} className="shrink-0" />
                  </div>
                  {item.description && <p className="text-xs text-app-muted line-clamp-2">{item.description}</p>}
                  <RatingSummary stat={stats[item.id]} />
                  <div className="flex items-center gap-3 text-xs text-app-muted mt-auto pt-2">
                    {item.address && <span className="truncate">{item.address}</span>}
                  </div>
                  <div className="flex gap-5 md:gap-2 pt-1 md:pt-2 border-t border-app-border">
                    <button onClick={() => setEditing({ ...item })} className="text-xs text-app-muted hover:text-app-fg transition-colors min-h-[40px] md:min-h-0">Edit</button>
                    <button onClick={() => setDeleting(item.id)} className="text-xs text-crimson/60 hover:text-crimson transition-colors min-h-[40px] md:min-h-0">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {filtered.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 bg-app-card border border-app-border rounded-xl px-4 py-3 hover:bg-app-card/80 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-app-fg truncate">{item.name}</h3>
                    <CategoryBadge label={catName(item.category_slug)} className="shrink-0" />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-app-muted mt-0.5">
                    <RatingSummary stat={stats[item.id]} />
                    {item.address && <span className="truncate">{item.address}</span>}
                  </div>
                </div>
                <div className="flex gap-3 shrink-0">
                  <button onClick={() => setEditing({ ...item })} className="text-xs text-app-muted hover:text-app-fg transition-colors">Edit</button>
                  <button onClick={() => setDeleting(item.id)} className="text-xs text-crimson/60 hover:text-crimson transition-colors">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {deleting && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => setDeleting(null)}>
          <div className="bg-app-card border border-app-border rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-app-fg mb-2">Delete place?</h3>
            <p className="text-sm text-app-muted mb-6">This won't affect trips that already use it.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleting(null)} className="text-sm text-app-muted hover:text-app-fg px-4 py-2">Cancel</button>
              <Button onClick={() => void handleDelete(deleting)} className="bg-crimson hover:bg-crimson/90">Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end md:items-center justify-center" onClick={() => setEditing(null)}>
          <div
            className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-5xl max-h-dvh-92 overflow-y-auto pb-safe-or-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-app-card z-10 px-5 pt-5 pb-3 border-b border-app-border flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-app-fg">{editing.id ? 'Edit place' : 'New place'}</h3>
              <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>

            <div className="px-5 py-5 flex flex-col gap-5">
              <PlaceDetailsEditor
                value={{
                  name: editing.name,
                  category: editing.category_slug,
                  address: editing.address,
                  latitude: editing.latitude,
                  longitude: editing.longitude,
                  description: editing.description,
                  tip: editing.tip,
                }}
                onChange={applyPlacePatch}
                categories={categories}
                photos={(
                  <PhotoGalleryField
                    photos={editing.photos ?? []}
                    onChange={(photos) => setEditing((prev) => (prev ? { ...prev, ...photoFields(photos) } : prev))}
                    onUpload={(file) => uploadPlacePhoto(user!.id, file)}
                    inputClassName={`${inputClass} py-1.5`}
                  />
                )}
              />

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-app-border">
                <button onClick={() => setEditing(null)} className="text-sm text-app-muted hover:text-app-fg">Cancel</button>
                <Button onClick={() => void handleSave()} disabled={saving || !editing.name.trim()}>
                  {saving ? 'Saving…' : editing.id ? 'Update' : 'Save to library'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast} />
    </div>
  );
}

export default EventLibrary;
